import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import {alpha, useTheme} from '@mui/material/styles';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  IconCallConsentBoth,
  IconCallConsentTick,
  IconQuickApplyCompany,
  InfoIcon,
} from '@jobseeker/lotus/assets/icons';

import {
  CALL_CONSENT_BOTH,
  CALL_CONSENT_COMPANIES_ONLY,
} from '@jobseeker/feature-profile/constants/callConsent';

type CallPreferenceOptionsProps = {
  value: number | null;
  onChange: (value: number) => void;
};

const OPTIONS = [
  {
    value: CALL_CONSENT_BOTH,
    title: 'Companies and consultants',
    subtitle: 'Every role matching your profile reaches you',
    badge: null,
    icon: IconCallConsentBoth,
    testId: 'call-consent-option-both',
  },
  {
    value: CALL_CONSENT_COMPANIES_ONLY,
    title: 'Companies only',
    subtitle: 'A narrower, more relevant set of roles',
    badge: null,
    icon: IconQuickApplyCompany,
    testId: 'call-consent-option-companies-only',
  },
];

const CallPreferenceOptions = ({
  value,
  onChange,
}: CallPreferenceOptionsProps) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      <Box display="flex" flexDirection="column" gap={1.5}>
        {OPTIONS.map(
          ({value: optionValue, title, subtitle, badge, icon, testId}) => {
            const selected = value === optionValue;
            return (
              <ButtonBase
                key={optionValue}
                onClick={() => onChange(optionValue)}
                data-testid={testId}
                data-component="call_consent_option"
                sx={{
                  display: 'flex',
                  gap: 1.75,
                  alignItems: 'center',
                  textAlign: 'left',
                  padding: '16px 18px',
                  borderRadius: 1.5,
                  border: `1.5px solid ${
                    selected ? primary : theme.palette.divider
                  }`,
                  backgroundColor: selected
                    ? alpha(primary, 0.06)
                    : theme.palette.background.paper,
                  boxShadow: selected
                    ? `0 4px 14px ${alpha(primary, 0.12)}`
                    : '0 1px 2px rgba(16,24,40,.04)',
                  transition:
                    'border-color .18s, background-color .18s, box-shadow .18s',
                }}
              >
                <Box
                  flexShrink={0}
                  width={40}
                  height={40}
                  borderRadius={1.25}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    backgroundColor: selected
                      ? alpha(primary, 0.12)
                      : theme.palette.action.hover,
                  }}
                >
                  <Image src={icon} alt="" width={19} height={19} />
                </Box>
                <Box display="flex" flexDirection="column" gap={0.25} flex={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography
                      fontSize={15.5}
                      fontWeight={600}
                      color={theme.palette.text.primary}
                    >
                      {title}
                    </Typography>
                    {badge && (
                      <Chip
                        label={badge}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: 11,
                          fontWeight: 600,
                          color: theme.palette.primary.dark,
                          backgroundColor: alpha(primary, 0.14),
                          '& .MuiChip-label': {px: 1},
                        }}
                      />
                    )}
                  </Box>
                  <Typography fontSize={13} color={theme.palette.text.secondary}>
                    {subtitle}
                  </Typography>
                </Box>
                <Box
                  flexShrink={0}
                  width={20}
                  height={20}
                  borderRadius="50%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    border: `1.5px solid ${
                      selected ? primary : theme.palette.divider
                    }`,
                    backgroundColor: selected ? primary : 'transparent',
                  }}
                >
                  {selected && (
                    <Image
                      src={IconCallConsentTick}
                      alt=""
                      width={12}
                      height={12}
                    />
                  )}
                </Box>
              </ButtonBase>
            );
          }
        )}
      </Box>

      <Box
        display="flex"
        gap={1.25}
        alignItems="flex-start"
        p={1.5}
        borderRadius={1.5}
        sx={{backgroundColor: theme.palette.action.hover}}
      >
        <Box flexShrink={0} display="flex" pt={0.25}>
          <Image src={InfoIcon} alt="" width={16} height={16} />
        </Box>
        <Typography
          fontSize={12.5}
          lineHeight={1.6}
          color={theme.palette.text.secondary}
        >
          <Box
            component="span"
            fontWeight={600}
            color={theme.palette.text.primary}
          >
            Consultants
          </Box>{' '}
          are third-party firms hired by companies to source profiles. The role
          is always at their client company.
        </Typography>
      </Box>
    </Box>
  );
};

export default CallPreferenceOptions;
